// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/RemediationGuidanceTab.jsx

import React, { useMemo } from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionEmptyState from "../shared/ExecutionEmptyState";
import ExecutionActionRow from "../shared/ExecutionActionRow";
import {
  buildFixPlanSteps,
  buildValidationText,
  getRemediationSummary,
  getRulePlanDiagnostic,
  inferProviderFromRule,
} from "../../../utils/remediationFixPlans";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function humanize(value, fallback = "—") {
  const text = clean(displayValue(value, ""));
  if (!text) return fallback;
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function listFrom(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => clean(displayValue(item, ""))).filter(Boolean);
  }
  const text = clean(displayValue(value, ""));
  if (!text) return [];
  return text
    .split(/\n|;|\u2022/g)
    .map((item) => item.replace(/^[-*\d.)\s]+/, "").trim())
    .filter(Boolean);
}

function uniqueList(...values) {
  const seen = new Set();
  const rows = [];

  values.flatMap(listFrom).forEach((item) => {
    const key = item.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(item);
  });

  return rows;
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function getTaskMetadata(task = {}) {
  const metadata = task?.task_metadata || task?.metadata || {};
  return asObject(metadata);
}

function getCopilotDraft(task = {}) {
  const metadata = getTaskMetadata(task);
  const draft = metadata?.copilot_draft || task?.copilot_draft || task?.copilotDraft || {};
  return asObject(draft);
}

function getGuidancePayload(guidanceContext = null) {
  const context = asObject(guidanceContext);
  return asObject(context.guidance || context.remediation_guidance || context.task_guidance || context);
}

function getGuidanceDraft(guidanceContext = null, task = {}) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const contextDraft = asObject(context.draft || context.remediation_draft || context.copilot_draft);
  const guidanceDraft = asObject(guidance.draft || guidance.remediation_draft || guidance.copilot_draft);
  const taskDraft = getCopilotDraft(task);

  return {
    ...taskDraft,
    ...contextDraft,
    ...guidanceDraft,
  };
}

function getRuleId(task = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);

  return clean(
    context.rule_id ||
      context.rule ||
      guidance.rule_id ||
      guidance.rule ||
      task.rule_id ||
      task.ruleId ||
      metadata.rule_id ||
      metadata.ruleId
  ).toUpperCase();
}

function getConnectorLabel(task = {}, diagnostic = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const source = clean(
    context.connector ||
      context.source ||
      guidance.connector ||
      guidance.source ||
      task.connector ||
      task.provider ||
      task.source ||
      task.source_kind ||
      task.stream ||
      getTaskMetadata(task).source
  );
  const rule = getRuleId(task, guidanceContext);
  const inferred = clean(diagnostic?.provider || inferProviderFromRule?.(rule));

  const normalized = clean(source || inferred).toLowerCase().replace(/[-\s]+/g, "_");

  if (normalized.includes("google_workspace") || rule.startsWith("GW_")) return "Google Workspace";
  if (normalized.includes("okta") || rule.startsWith("OKTA_")) return "Okta";
  if (normalized.includes("github") || rule.startsWith("GH_")) return "GitHub";
  if (normalized.includes("cloudflare") || rule.startsWith("CF_")) return "Cloudflare";
  if (normalized.includes("aws") || rule.startsWith("S3_") || rule.includes("IAM") || rule.includes("CLOUDTRAIL")) return "AWS";
  if (source) return humanize(source, "Connector");
  return "Connector";
}

function getResourceId(task = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);

  return clean(
    context.resource_id ||
      context.resource ||
      guidance.resource_id ||
      guidance.resource ||
      task.resource_id ||
      task.resourceId ||
      metadata.resource_id ||
      metadata.resourceId
  );
}

function getProblemSummary(task = {}, connector = "Connector", guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const rule = getRuleId(task, guidanceContext) || "rule";
  const control = clean(
    context.control_id ||
      context.control ||
      guidance.control_id ||
      guidance.control ||
      task.control_id ||
      task.controlId ||
      metadata.control_id ||
      metadata.controlId
  );
  const framework = clean(context.framework || guidance.framework || task.framework || metadata.framework);
  const resource = getResourceId(task, guidanceContext);

  const existing = clean(
    context.problem_summary ||
      guidance.problem_summary ||
      task.problem_summary ||
      metadata.problem_summary ||
      metadata.finding_summary ||
      task.reason_summary ||
      task.summary ||
      task.description ||
      getRemediationSummary(task)
  );

  if (existing && !existing.toLowerCase().includes("apply connector-specific remediation")) {
    return existing;
  }

  const subject = resource ? `${rule} is failing for ${resource}` : `${rule} is failing`;
  const frameworkText = framework || control ? ` ${[framework, control].filter(Boolean).join(" ")} currently requires remediation.` : "";
  return `${connector} finding detected: ${subject}.${frameworkText}`;
}

function getRiskSummary(task = {}, connector = "Connector", guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);
  const riskNotes = listFrom(context.risk_summary || guidance.risk_summary || context.risk_notes || draft.risk_notes || metadata.risk_notes || task.risk_notes);

  if (riskNotes.length) return riskNotes[0];

  const rule = getRuleId(task, guidanceContext);
  const resource = getResourceId(task, guidanceContext) || "the affected resource";

  if (rule.includes("VERSIONING")) {
    return `Without versioning, deleted or overwritten source evidence for ${resource} may not be recoverable. This weakens audit integrity, incident investigation, and evidence preservation.`;
  }

  if (rule.includes("MFA") || rule.includes("2SV")) {
    return `Without strong authentication, ${connector} accounts are more exposed to credential compromise and unauthorized administrative access.`;
  }

  if (rule.includes("WAF")) {
    return `Without a WAF baseline, ${resource} is more exposed to common web attacks, automated probing, and application-layer abuse.`;
  }

  if (rule.includes("DNSSEC")) {
    return `Without DNSSEC, DNS responses for ${resource} have weaker protection against spoofing and integrity attacks.`;
  }

  if (rule.includes("TLS") || rule.includes("HTTPS")) {
    return `Weak or inconsistent transport security can reduce confidentiality and trust for traffic associated with ${resource}.`;
  }

  if (rule.includes("PUBLIC")) {
    return `Public exposure can allow unintended access to ${resource} and can weaken confidentiality, incident containment, and compliance evidence.`;
  }

  if (rule.includes("SECRET") || rule.includes("DEPENDABOT") || rule.includes("BRANCH")) {
    return `Repository governance gaps can reduce software supply-chain visibility and make unauthorized or vulnerable changes harder to detect.`;
  }

  if (rule.includes("ACCESS_KEY") || rule.includes("TRUST_POLICY") || rule.includes("ADMIN_POLICY") || rule.includes("WILDCARD")) {
    return `Identity and access governance gaps can increase the likelihood of excessive privilege, stale access, and unauthorized cloud activity.`;
  }

  return `This finding can weaken the control mapped to the selected framework until the source configuration is corrected and validation confirms the condition has cleared.`;
}

function getEvidenceRequired(task = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);

  return uniqueList(
    context.evidence_required,
    guidance.evidence_required,
    context.evidence_checklist,
    guidance.evidence_checklist,
    context.draft?.evidence_checklist,
    guidance.draft?.evidence_checklist,
    draft.evidence_checklist,
    metadata.evidence_checklist,
    task.evidence_checklist,
    task.evidence_required,
    task.required_evidence,
    [
      "Before/after source-system configuration evidence.",
      "Connector sync or assessment output after remediation.",
      "OASYS validation result for the affected rule/resource.",
      "Current evidence snapshot linked to the remediation task.",
    ]
  );
}

function getValidationChecklist(task = {}, validationText = "", guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);

  return uniqueList(
    context.validation_steps,
    guidance.validation_steps,
    context.validation_checklist,
    guidance.validation_checklist,
    context.draft?.validation_steps,
    guidance.draft?.validation_steps,
    draft.validation_steps,
    metadata.validation_steps,
    task.validation_steps,
    validationText,
    metadata.validation,
    [
      "Run Validation from Mission Execution after remediation.",
      "Expected result: PASS.",
      "Confirm the closure guard clears only after validation succeeds.",
    ]
  );
}

function getRelatedControls(task = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const direct = uniqueList(
    context.related_controls,
    guidance.related_controls,
    task.related_controls,
    task.relatedControls,
    metadata.related_controls,
    metadata.relatedControls
  );

  const framework = clean(context.framework || guidance.framework || task.framework || metadata.framework);
  const control = clean(context.control_id || context.control || guidance.control_id || guidance.control || task.control_id || task.controlId || metadata.control_id || metadata.controlId);

  if (framework || control) {
    const primary = [framework, control].filter(Boolean).join(" ");
    return uniqueList(primary, direct);
  }

  return direct;
}

function getEstimatedTime(task = {}, diagnostic = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);

  return (
    clean(context.estimated_time || guidance.estimated_time || task.estimated_time || task.estimatedTime || metadata.estimated_time || draft.estimated_time) ||
    (diagnostic?.is_generic_fallback ? "10–20 minutes" : "5–10 minutes")
  );
}

function getPrerequisites(task = {}, guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);

  return uniqueList(
    context.prerequisites,
    guidance.prerequisites,
    context.draft?.prerequisites,
    guidance.draft?.prerequisites,
    draft.prerequisites,
    metadata.prerequisites,
    task.prerequisites,
    [
      "Access to the affected source system.",
      "Permission to update the affected configuration or document an approved exception.",
      "Current connector sync capability for post-fix validation.",
    ]
  );
}

function getReferences(task = {}, connector = "Connector", guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const metadata = getTaskMetadata(task);
  const draft = getGuidanceDraft(context, task);
  const direct = uniqueList(context.references, guidance.references, context.draft?.references, guidance.draft?.references, draft.references, metadata.references, task.references);

  if (direct.length) return direct;

  if (connector === "AWS") return ["AWS documentation for the affected service.", "OASYS validation and evidence guidance."];
  if (connector === "Google Workspace") return ["Google Workspace Admin Help.", "OASYS Google Workspace connector guidance."];
  if (connector === "Okta") return ["Okta Admin Console documentation.", "OASYS Okta connector guidance."];
  if (connector === "GitHub") return ["GitHub repository security documentation.", "OASYS GitHub connector guidance."];
  if (connector === "Cloudflare") return ["Cloudflare dashboard documentation.", "OASYS Cloudflare connector guidance."];

  return ["OASYS remediation guidance.", "Source-system documentation for the affected connector."];
}

function cleanGuidanceStatus(value = "", connector = "Connector", rule = "") {
  const text = clean(value);
  const ruleText = clean(rule);

  if (!text || text.toLowerCase().includes("shared fix plan guidance")) {
    if (ruleText.startsWith("S3_")) return "S3 Remediation";
    if (ruleText.includes("IAM")) return "IAM Remediation";
    if (ruleText.includes("CLOUDTRAIL")) return "CloudTrail Remediation";
    if (ruleText.startsWith("CF_")) return "Cloudflare Remediation";
    if (ruleText.startsWith("GH_")) return "GitHub Remediation";
    if (ruleText.startsWith("GW_")) return "Google Workspace Remediation";
    if (ruleText.startsWith("OKTA_")) return "Okta Remediation";
    if (connector && connector !== "Connector") return `${connector} Remediation`;
    return "Remediation";
  }

  return text
    .replace(/specific remediation plan/gi, "Remediation")
    .replace(/shared fix plan guidance/gi, "Remediation")
    .replace(/\s+/g, " ")
    .trim();
}

function splitStepTitle(step = "") {
  const text = clean(step);
  if (!text) return { title: "Remediation step", detail: "" };

  const colonIndex = text.indexOf(":");
  if (colonIndex > 3 && colonIndex <= 70) {
    return {
      title: text.slice(0, colonIndex).trim(),
      detail: text.slice(colonIndex + 1).trim(),
    };
  }

  return { title: text.replace(/[.]+$/, ""), detail: "" };
}

function isGenericGuidanceText(value = "") {
  const text = clean(value).toLowerCase();
  if (!text) return true;

  const genericMarkers = [
    "review the failing control or rule evidence",
    "open the governing sop",
    "assign an owner and document the remediation plan",
    "execute the remediation steps under change-control expectations",
    "collect evidence before and after remediation",
    "run oasys validation before closing the task",
    "open the fix plan",
    "complete the source-system remediation or approved exception documentation",
    "move the task to ready for validation",
    "run full cloud assessment to verify the condition is cleared",
    "apply the connector-specific remediation",
    "complete this remediation step in the affected source system",
  ];

  return genericMarkers.some((marker) => text.includes(marker));
}

function hasSpecificBackendSteps(steps = []) {
  const rows = Array.isArray(steps) ? steps.filter(Boolean) : [];
  if (!rows.length) return false;

  const specificRows = rows.filter((stepItem) => !isGenericGuidanceText(displayValue(stepItem, "")));
  return specificRows.length > 0 && specificRows.length >= Math.ceil(rows.length / 2);
}

function step(title, detail, target = "") {
  return { title, detail, target };
}

function s3VersioningStepCards({ resource = "", validationChecklist = [] } = {}) {
  const bucket = clean(resource) || "the affected S3 bucket";
  const validationText = validationChecklist[0] || "Validation passes when refreshed AWS S3 posture shows versioning is enabled for the affected bucket.";

  return [
    step("Open Amazon S3", "Sign in to the AWS account that owns the affected bucket and open the Amazon S3 console.", "AWS Console → S3"),
    step("Open the affected bucket", `Select the bucket reported by OASYS: ${bucket}.`, bucket),
    step("Open Properties", "In the bucket navigation, open the Properties tab and locate the Bucket Versioning setting.", "Bucket → Properties → Bucket Versioning"),
    step("Enable Bucket Versioning", "Choose Edit, set Bucket Versioning to Enabled, and save the configuration.", "Expected state: Enabled"),
    step("Confirm the saved setting", "Refresh the bucket properties page and confirm the bucket now shows versioning as enabled.", "Evidence: updated bucket configuration"),
    step("Confirm lifecycle and retention expectations", "Because this bucket stores CloudTrail logs, confirm lifecycle, retention, and governing SOP expectations still match the control requirement.", "Evidence: lifecycle or retention review"),
    step("Refresh connector evidence", "Run the AWS connector sync or Full Cloud Assessment so OASYS receives the updated S3 bucket state.", "Evidence: connector sync or assessment output"),
    step("Run validation", validationText, "Expected result: PASS"),
  ];
}

function s3LoggingStepCards({ resource = "", validationChecklist = [] } = {}) {
  const bucket = clean(resource) || "the affected S3 bucket";
  const validationText = validationChecklist[0] || "Validation passes when refreshed AWS S3 posture shows server access logging is enabled for the affected bucket.";

  return [
    step("Open Amazon S3", "Sign in to the AWS account that owns the affected bucket and open the Amazon S3 console.", "AWS Console → S3"),
    step("Open the affected bucket", `Select the bucket reported by OASYS: ${bucket}.`, bucket),
    step("Open Properties", "In the bucket navigation, open the Properties tab and locate Server access logging.", "Bucket → Properties → Server access logging"),
    step("Enable server access logging", "Choose Edit, enable server access logging, and select the approved target logging bucket/prefix.", "Expected state: Logging enabled"),
    step("Confirm target bucket permissions", "Confirm the logging target bucket allows S3 log delivery and matches the tenant-approved logging pattern.", "Evidence: target bucket and policy review"),
    step("Refresh connector evidence", "Run the AWS connector sync or Full Cloud Assessment so OASYS receives the updated S3 logging state.", "Evidence: connector sync or assessment output"),
    step("Run validation", validationText, "Expected result: PASS"),
  ];
}

function s3SecurityStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const bucket = clean(resource) || "the affected S3 bucket";
  const validationText = validationChecklist[0] || `Validation passes when refreshed AWS S3 posture shows ${rule || "the affected S3 setting"} is remediated for the affected bucket.`;

  if (rule === "S3_PUBLIC_ACCESS_BLOCK") {
    return [
      step("Open Amazon S3", "Sign in to the AWS account that owns the affected bucket and open the Amazon S3 console.", "AWS Console → S3"),
      step("Open the affected bucket", `Select the bucket reported by OASYS: ${bucket}.`, bucket),
      step("Open Permissions", "Open the Permissions tab and review Block public access settings.", "Bucket → Permissions → Block public access"),
      step("Enable public access block", "Enable the tenant-approved Block Public Access settings unless a documented exception is required.", "Expected state: Public access blocked"),
      step("Review bucket policy and ACLs", "Confirm bucket policy, object ownership, and ACLs do not create unintended public exposure.", "Evidence: permissions review"),
      step("Refresh connector evidence", "Run the AWS connector sync or Full Cloud Assessment so OASYS receives the updated S3 permissions state.", "Evidence: connector sync or assessment output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "S3_POLICY_PUBLIC") {
    return [
      step("Open Amazon S3", "Sign in to the AWS account that owns the affected bucket and open the Amazon S3 console.", "AWS Console → S3"),
      step("Open the affected bucket", `Select the bucket reported by OASYS: ${bucket}.`, bucket),
      step("Review bucket policy", "Open Permissions and inspect the bucket policy for public principals, broad conditions, or wildcard access.", "Bucket → Permissions → Bucket policy"),
      step("Remove public access", "Update the policy to remove unintended public access or replace it with tenant-approved scoped access.", "Expected state: No public bucket policy exposure"),
      step("Confirm access path", "Confirm required applications still have least-privilege access after policy changes.", "Evidence: access review"),
      step("Refresh connector evidence", "Run the AWS connector sync or Full Cloud Assessment so OASYS receives the updated policy state.", "Evidence: connector sync or assessment output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  return [
    step("Open Amazon S3", "Sign in to the AWS account that owns the affected bucket and open the Amazon S3 console.", "AWS Console → S3"),
    step("Open the affected bucket", `Select the bucket reported by OASYS: ${bucket}.`, bucket),
    step("Review encryption configuration", "Open Properties and locate Default encryption for the affected bucket.", "Bucket → Properties → Default encryption"),
    step("Enable approved encryption", "Enable the tenant-approved encryption setting, such as SSE-S3 or the approved KMS key.", "Expected state: Default encryption enabled"),
    step("Confirm encryption evidence", "Refresh the bucket properties page and confirm the encryption setting is saved.", "Evidence: updated bucket encryption configuration"),
    step("Refresh connector evidence", "Run the AWS connector sync or Full Cloud Assessment so OASYS receives the updated S3 encryption state.", "Evidence: connector sync or assessment output"),
    step("Run validation", validationText, "Expected result: PASS"),
  ];
}

function cloudflareStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const zone = clean(resource) || "the affected Cloudflare zone";
  const validationText = validationChecklist[0] || `Validation passes when refreshed Cloudflare evidence shows ${rule || "the affected setting"} is remediated for ${zone}.`;

  if (rule === "CF_WAF_DISABLED") {
    return [
      step("Open Cloudflare dashboard", "Sign in to Cloudflare and select the affected zone reported by OASYS.", zone),
      step("Open WAF settings", "Navigate to Security and open the WAF configuration for the zone.", "Security → WAF"),
      step("Review current WAF state", "Confirm whether the Cloudflare WAF ruleset is disabled, not deployed, or missing the approved baseline.", "Evidence: current WAF state"),
      step("Enable the approved WAF baseline", "Enable the tenant-approved Cloudflare Managed Ruleset or approved WAF profile for the zone.", "Expected state: WAF enabled"),
      step("Confirm rule deployment", "Confirm the WAF ruleset is active and deployed for the intended hostname/path scope.", "Evidence: active WAF ruleset"),
      step("Run Cloudflare edge sync", "Refresh Cloudflare connector evidence or run the Cloudflare edge sync so OASYS receives the updated state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "CF_DNSSEC_DISABLED") {
    return [
      step("Open Cloudflare dashboard", "Sign in to Cloudflare and select the affected zone reported by OASYS.", zone),
      step("Open DNSSEC settings", "Navigate to DNS and open DNSSEC settings for the zone.", "DNS → Settings → DNSSEC"),
      step("Review DNSSEC status", "Confirm DNSSEC is disabled or not fully active for the zone.", "Evidence: current DNSSEC status"),
      step("Enable DNSSEC", "Enable DNSSEC and capture the DS record details Cloudflare provides.", "Expected state: DNSSEC enabled"),
      step("Update registrar DS record", "Add or confirm the DS record at the domain registrar when required for activation.", "Evidence: registrar DS record"),
      step("Confirm active status", "Return to Cloudflare and confirm DNSSEC reaches active status after propagation.", "Evidence: active DNSSEC status"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "CF_ALWAYS_HTTPS_DISABLED") {
    return [
      step("Open Cloudflare dashboard", "Sign in to Cloudflare and select the affected zone reported by OASYS.", zone),
      step("Open SSL/TLS edge certificates", "Navigate to SSL/TLS and open Edge Certificates.", "SSL/TLS → Edge Certificates"),
      step("Review Always Use HTTPS", "Confirm Always Use HTTPS is disabled or not enforced for the affected zone.", "Evidence: current HTTPS redirect state"),
      step("Enable Always Use HTTPS", "Turn on Always Use HTTPS or the tenant-approved HTTPS redirect policy.", "Expected state: Always Use HTTPS enabled"),
      step("Confirm no redirect conflict", "Confirm redirects do not conflict with application routing or origin configuration.", "Evidence: redirect review"),
      step("Run Cloudflare edge sync", "Refresh Cloudflare connector evidence so OASYS receives the updated zone setting.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  return [
    step("Open Cloudflare dashboard", "Sign in to Cloudflare and select the affected zone reported by OASYS.", zone),
    step("Open SSL/TLS settings", "Navigate to SSL/TLS and review the zone encryption mode.", "SSL/TLS → Overview"),
    step("Review current TLS mode", "Confirm whether the current SSL/TLS mode is weaker than the tenant-approved baseline.", "Evidence: current TLS mode"),
    step("Set approved TLS mode", "Set SSL/TLS mode to the approved strict configuration, such as Full (strict), when supported by the origin.", "Expected state: strict TLS baseline"),
    step("Confirm origin certificate readiness", "Confirm the origin certificate is valid so strict mode does not break application availability.", "Evidence: origin certificate check"),
    step("Run Cloudflare edge sync", "Refresh Cloudflare connector evidence so OASYS receives the updated TLS state.", "Evidence: connector sync output"),
    step("Run validation", validationText, "Expected result: PASS"),
  ];
}

function githubStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const repo = clean(resource) || "the affected GitHub repository";
  const validationText = validationChecklist[0] || `Validation passes when refreshed GitHub evidence shows ${rule || "the affected repository setting"} is remediated for ${repo}.`;

  if (rule === "GH_DEPENDABOT_DISABLED") {
    return [
      step("Open GitHub repository", "Sign in to GitHub and open the affected repository reported by OASYS.", repo),
      step("Open code security settings", "Navigate to repository Settings and open Code security and analysis.", "Settings → Code security and analysis"),
      step("Review Dependabot alert state", "Confirm Dependabot alerts are disabled or not configured for the repository.", "Evidence: current Dependabot state"),
      step("Enable Dependabot alerts", "Enable Dependabot alerts for the repository using the tenant-approved security baseline.", "Expected state: Dependabot alerts enabled"),
      step("Enable related dependency protections", "Confirm dependency graph and security updates are enabled when required by policy.", "Evidence: dependency security settings"),
      step("Refresh GitHub evidence", "Run the GitHub connector sync so OASYS receives the updated repository security state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "GH_SECRET_SCANNING_DISABLED") {
    return [
      step("Open GitHub repository", "Sign in to GitHub and open the affected repository reported by OASYS.", repo),
      step("Open code security settings", "Navigate to repository Settings and open Code security and analysis.", "Settings → Code security and analysis"),
      step("Review secret scanning state", "Confirm secret scanning is disabled or missing for the repository.", "Evidence: current secret scanning state"),
      step("Enable secret scanning", "Enable GitHub secret scanning using the tenant-approved repository security baseline.", "Expected state: Secret scanning enabled"),
      step("Review alert routing", "Confirm alerts route to the expected maintainers or security owners.", "Evidence: alert owner review"),
      step("Refresh GitHub evidence", "Run the GitHub connector sync so OASYS receives the updated repository security state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "GH_BRANCH_PROTECTION_DISABLED" || rule === "GH_REQUIRED_REVIEWS_DISABLED") {
    return [
      step("Open GitHub repository", "Sign in to GitHub and open the affected repository reported by OASYS.", repo),
      step("Open branch protection rules", "Navigate to repository Settings and open Branches or Rulesets.", "Settings → Branches / Rulesets"),
      step("Review protected branch coverage", "Confirm the default branch is missing the approved protection rule or required reviews.", "Evidence: current branch protection state"),
      step("Enable branch protection", "Add or update the branch protection rule or ruleset for the default branch.", "Expected state: protected default branch"),
      step("Require pull request review", "Enable required pull request reviews and any tenant-approved checks before merge.", "Evidence: required reviews/checks enabled"),
      step("Refresh GitHub evidence", "Run the GitHub connector sync so OASYS receives the updated repository governance state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  return [];
}

function googleWorkspaceStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const subject = clean(resource) || "the affected Google Workspace user or admin scope";
  const validationText = validationChecklist[0] || `Validation passes when refreshed Google Workspace evidence shows ${rule || "the affected setting"} is remediated.`;

  if (rule === "GW_2SV_DISABLED") {
    return [
      step("Open Google Admin console", "Sign in to the Google Admin console with an account authorized to manage user security.", "Admin Console → Directory"),
      step("Find the affected user or org unit", `Locate the user or organizational unit reported by OASYS: ${subject}.`, subject),
      step("Review 2-Step Verification state", "Confirm 2-Step Verification is disabled, unenforced, or outside the tenant-approved baseline.", "Evidence: current 2SV state"),
      step("Enable or enforce 2-Step Verification", "Enable 2-Step Verification for the affected user or apply the approved org-unit enforcement policy.", "Expected state: 2SV enabled/enforced"),
      step("Confirm enrollment", "Confirm the affected account is enrolled or covered by the enforcement policy.", "Evidence: user security state"),
      step("Refresh Google Workspace evidence", "Run the Google Workspace connector sync so OASYS receives the updated security state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "GW_SUPER_ADMIN_EXPOSURE") {
    return [
      step("Open Google Admin console", "Sign in to the Google Admin console with super admin review authority.", "Admin Console → Directory → Users"),
      step("Review super admin assignments", "Identify the super admin account or group reported by OASYS.", subject),
      step("Confirm business need", "Confirm whether the account still requires super admin privileges.", "Evidence: owner/access review"),
      step("Remove excess privilege", "Remove super admin privileges or replace them with the least-privilege admin role required.", "Expected state: least privilege"),
      step("Document exception if needed", "If privilege must remain, document business owner, purpose, compensating controls, and review date.", "Evidence: approved exception"),
      step("Refresh Google Workspace evidence", "Run the Google Workspace connector sync so OASYS receives the updated admin role state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  return [];
}

function oktaStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const subject = clean(resource) || "the affected Okta user, group, or policy";
  const validationText = validationChecklist[0] || `Validation passes when refreshed Okta evidence shows ${rule || "the affected setting"} is remediated.`;

  if (rule === "OKTA_MFA_DISABLED") {
    return [
      step("Open Okta Admin Console", "Sign in to Okta Admin Console with permissions to manage authentication policies.", "Okta Admin → Security"),
      step("Locate the affected policy or user", `Find the user, group, or policy reported by OASYS: ${subject}.`, subject),
      step("Review MFA enforcement", "Confirm MFA is disabled, unenforced, or not assigned to the affected population.", "Evidence: current MFA policy state"),
      step("Enable MFA enforcement", "Apply the tenant-approved MFA policy to the affected group, app, or user population.", "Expected state: MFA enforced"),
      step("Confirm policy assignment", "Confirm the rule is active and assigned to the intended users/apps.", "Evidence: active policy assignment"),
      step("Refresh Okta evidence", "Run the Okta connector sync so OASYS receives the updated authentication policy state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "OKTA_PRIVILEGED_GROUP_EXPOSURE") {
    return [
      step("Open Okta Admin Console", "Sign in to Okta Admin Console with permissions to review groups and admin roles.", "Okta Admin → Directory / Security"),
      step("Locate the privileged group", `Find the privileged group or admin assignment reported by OASYS: ${subject}.`, subject),
      step("Review members and role assignments", "Confirm whether group membership or assigned admin privileges exceed tenant policy.", "Evidence: current membership/role state"),
      step("Remove unnecessary privilege", "Remove users or admin assignments that do not have current business justification.", "Expected state: least privilege"),
      step("Document exception if needed", "If privileged access must remain, document owner, purpose, compensating controls, and review date.", "Evidence: approved exception"),
      step("Refresh Okta evidence", "Run the Okta connector sync so OASYS receives the updated privileged access state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  return [];
}

function iamStepCards({ rule = "", resource = "", validationChecklist = [] } = {}) {
  const target = clean(resource) || "the affected IAM identity";
  const validationText = validationChecklist[0] || `Validation passes when refreshed AWS IAM evidence shows ${rule || "the affected IAM condition"} is remediated for ${target}.`;

  if (rule === "ROOT_MFA") {
    return [
      step("Open AWS Account security credentials", "Sign in as an authorized account administrator and open the AWS account security credentials page.", "AWS Console → Account → Security credentials"),
      step("Review root MFA state", "Confirm the root account does not have MFA enabled.", "Evidence: current root MFA state"),
      step("Enable MFA for root", "Register the tenant-approved MFA device for the root account.", "Expected state: root MFA enabled"),
      step("Secure break-glass records", "Store recovery and break-glass documentation according to the governing SOP.", "Evidence: break-glass review"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated root MFA state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "IAM_USER_MFA" || rule === "IAM_USER_HAS_NO_MFA_AND_ACTIVE_KEY") {
    return [
      step("Open AWS IAM", "Sign in to the AWS account and open IAM.", "AWS Console → IAM"),
      step("Go to Users", `Locate the affected IAM user reported by OASYS: ${target}.`, target),
      step("Review MFA and access key state", "Confirm whether the user has console access, active access keys, and no MFA device assigned.", "Evidence: current user security state"),
      step("Confirm access requirement", "Confirm whether the user still requires console access or programmatic access.", "Evidence: owner/access review"),
      step("Enroll MFA or remove unnecessary access", "Attach an MFA device for required console users, or remove console/password access where not needed.", "Expected state: MFA enforced or console access removed"),
      step("Review exception requirements", "If MFA cannot be enabled, document business owner, purpose, compensating controls, and review date before approval.", "Evidence: approved exception if needed"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated IAM user state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  if (rule.includes("ACCESS_KEY")) {
    return [
      step("Open AWS IAM", "Sign in to the AWS account and open IAM.", "AWS Console → IAM"),
      step("Locate the affected identity", `Open the IAM user and access key reported by OASYS: ${target}.`, target),
      step("Review key age and usage", "Review last used date, age, application owner, and whether the key is still required.", "Evidence: access key review"),
      step("Rotate or deactivate the key", "Rotate the key through the approved secret-management process or deactivate/delete it if unused.", "Expected state: stale or unnecessary key removed"),
      step("Confirm dependent services", "Confirm dependent workloads have moved to the new key or to role-based access before deleting active credentials.", "Evidence: owner confirmation"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated key state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS"),
    ];
  }

  if (rule === "IAM_ROLE_UNUSED_90D") {
    return [
      step("Open AWS IAM Roles", "Sign in to the AWS account and open IAM Roles.", "AWS Console → IAM → Roles"),
      step("Locate the affected role", `Open the role reported by OASYS: ${target}.`, target),
      step("Review last activity", "Confirm the role has not been used within the tenant-approved activity window.", "Evidence: access advisor / last used review"),
      step("Confirm owner and dependency", "Confirm whether any workload, vendor, or automation still depends on this role.", "Evidence: owner dependency review"),
      step("Disable or remove the role", "Remove the role if unused, or document an approved exception with owner, purpose, and review date.", "Expected state: unused role removed or exception documented"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated role state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  if (rule === "IAM_ROLE_TRUST_POLICY_EXTERNAL_ACCOUNT") {
    return [
      step("Open AWS IAM Roles", "Sign in to the AWS account and open IAM Roles.", "AWS Console → IAM → Roles"),
      step("Locate the affected role", `Open the role reported by OASYS: ${target}.`, target),
      step("Review trust relationship", "Open the Trust relationships tab and review external account principals and conditions.", "Evidence: current trust policy"),
      step("Confirm external access need", "Confirm the external account, vendor, or integration still has an approved business purpose.", "Evidence: owner/vendor review"),
      step("Restrict or remove external trust", "Remove unnecessary external principals or add tenant-approved controls such as ExternalId and scoped conditions.", "Expected state: approved scoped trust only"),
      step("Document exception if needed", "If external trust must remain, document business owner, purpose, compensating controls, and review date.", "Evidence: approved exception"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated trust policy state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  if (rule.includes("ADMIN_POLICY") || rule.includes("WILDCARD") || rule.includes("POLICY")) {
    return [
      step("Open AWS IAM", "Sign in to the AWS account and open IAM.", "AWS Console → IAM"),
      step("Locate the affected identity or policy", `Find the user, role, group, or policy reported by OASYS: ${target}.`, target),
      step("Review effective permissions", "Review attached managed policies, inline policies, wildcards, and administrative privileges.", "Evidence: current permission state"),
      step("Confirm business requirement", "Confirm whether the identity still requires administrative or wildcard access.", "Evidence: owner/access review"),
      step("Reduce permissions", "Replace administrative or wildcard access with the least-privilege policy required for the workload.", "Expected state: least privilege"),
      step("Document exception if needed", "If broad access must remain, document owner, purpose, compensating controls, Access Advisor review, CloudTrail review, and review date.", "Evidence: approved exception"),
      step("Refresh IAM evidence", "Run the AWS IAM connector sync or Full Cloud Assessment so OASYS receives the updated permission state.", "Evidence: connector sync output"),
      step("Run validation", validationText, "Expected result: PASS or approved exception"),
    ];
  }

  return [];
}

function ruleSpecificStepCards({ rule = "", resource = "", connector = "", validationChecklist = [] } = {}) {
  const normalizedRule = clean(rule).toUpperCase();

  if (normalizedRule === "S3_VERSIONING") {
    return s3VersioningStepCards({ resource, validationChecklist });
  }

  if (normalizedRule === "S3_LOGGING") {
    return s3LoggingStepCards({ resource, validationChecklist });
  }

  if (["S3_ENCRYPTION", "S3_PUBLIC_ACCESS_BLOCK", "S3_POLICY_PUBLIC"].includes(normalizedRule)) {
    return s3SecurityStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  if (normalizedRule.startsWith("CF_") || connector === "Cloudflare") {
    return cloudflareStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  if (normalizedRule.startsWith("GH_") || connector === "GitHub") {
    return githubStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  if (normalizedRule.startsWith("GW_") || connector === "Google Workspace") {
    return googleWorkspaceStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  if (normalizedRule.startsWith("OKTA_") || connector === "Okta") {
    return oktaStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  if (normalizedRule.includes("IAM") || normalizedRule === "ROOT_MFA" || connector === "AWS") {
    return iamStepCards({ rule: normalizedRule, resource, validationChecklist });
  }

  return [];
}

function buildRecommendedStepCardsFromSteps(steps = []) {
  return (steps || []).map((stepItem) => {
    if (stepItem && typeof stepItem === "object") {
      return {
        title: clean(stepItem.title || stepItem.label || stepItem.name) || "Remediation step",
        detail: clean(stepItem.detail || stepItem.description || stepItem.message || stepItem.value) || displayValue(stepItem),
        target: clean(stepItem.target || stepItem.expected_state || stepItem.evidence || stepItem.validation),
      };
    }

    const parsed = splitStepTitle(stepItem);
    return {
      title: parsed.title,
      detail: parsed.detail || "Complete this remediation step in the affected source system.",
      target: "",
    };
  });
}

function getBackendRecommendedSteps(guidanceContext = null) {
  const context = asObject(guidanceContext);
  const guidance = getGuidancePayload(context);
  const draft = getGuidanceDraft(context);

  return uniqueList(
    context.recommended_steps,
    context.recommendedSteps,
    context.recommended_actions,
    context.recommendedActions,
    guidance.recommended_steps,
    guidance.recommendedSteps,
    guidance.recommended_actions,
    guidance.recommendedActions,
    context.draft?.recommended_actions,
    context.draft?.recommendedActions,
    context.draft?.recommended_steps,
    context.draft?.recommendedSteps,
    guidance.draft?.recommended_actions,
    guidance.draft?.recommendedActions,
    guidance.draft?.recommended_steps,
    guidance.draft?.recommendedSteps,
    draft.recommended_actions,
    draft.recommendedActions,
    draft.recommended_steps,
    draft.recommendedSteps
  );
}

function getTaskFallbackSteps(task = {}) {
  const draft = getCopilotDraft(task);

  return uniqueList(
    draft.recommended_actions,
    draft.recommendedActions,
    draft.recommended_steps,
    draft.recommendedSteps,
    task.remediation_steps,
    task.guidance_steps,
    task.fix_steps,
    buildFixPlanSteps(task, [], task)
  );
}

function chooseRecommendedStepCards({ task = {}, guidanceContext = null, rule = "", connector = "", resource = "", validationChecklist = [] } = {}) {
  const backendSteps = getBackendRecommendedSteps(guidanceContext);
  const backendIsSpecific = hasSpecificBackendSteps(backendSteps);
  const specificCards = ruleSpecificStepCards({ rule, connector, resource, validationChecklist });

  if (backendIsSpecific) {
    return {
      source: "backend_guidance",
      steps: backendSteps,
      cards: buildRecommendedStepCardsFromSteps(backendSteps),
    };
  }

  if (specificCards.length) {
    return {
      source: "mission_execution_rule_specific",
      steps: specificCards.map((item) => `${item.title}: ${item.detail}`),
      cards: specificCards,
    };
  }

  const fallbackSteps = getTaskFallbackSteps(task);
  const cleanedFallback = fallbackSteps.filter((item) => !isGenericGuidanceText(item));

  if (cleanedFallback.length) {
    return {
      source: "task_fallback",
      steps: cleanedFallback,
      cards: buildRecommendedStepCardsFromSteps(cleanedFallback),
    };
  }

  return {
    source: "generic_fallback",
    steps: fallbackSteps,
    cards: buildRecommendedStepCardsFromSteps(fallbackSteps),
  };
}

function SectionList({ items = [], ordered = false }) {
  const rows = Array.isArray(items) ? items.filter(Boolean) : [];

  if (!rows.length) {
    return <ExecutionEmptyState title="No details available" message="No additional details are available for this section yet." />;
  }

  const Tag = ordered ? "ol" : "ul";

  return (
    <Tag style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.55, margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
      {rows.map((item, index) => (
        <li key={`${ordered ? "ordered" : "list"}-${index}`}>{displayValue(item)}</li>
      ))}
    </Tag>
  );
}

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
};

const metricStyle = {
  border: "1px solid rgba(96, 165, 250, 0.2)",
  borderRadius: 14,
  background: "rgba(15, 23, 42, 0.38)",
  padding: "12px 14px",
  display: "grid",
  gap: 5,
  minWidth: 0,
};

const labelStyle = {
  color: "#93c5fd",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: ".05em",
};

const valueStyle = {
  color: "#f8fafc",
  fontSize: 13,
  fontWeight: 850,
  overflowWrap: "anywhere",
};

const paragraphStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
  overflowWrap: "anywhere",
};

const stepGridStyle = {
  display: "grid",
  gap: 10,
};

const stepCardStyle = {
  border: "1px solid rgba(96, 165, 250, 0.22)",
  borderRadius: 16,
  background: "rgba(15, 23, 42, 0.42)",
  padding: "14px 16px",
  display: "grid",
  gap: 8,
};

const stepHeaderStyle = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
};

const stepNumberStyle = {
  width: 26,
  height: 26,
  borderRadius: 999,
  border: "1px solid rgba(96, 165, 250, 0.45)",
  background: "rgba(37, 99, 235, 0.18)",
  color: "#dbeafe",
  fontSize: 12,
  fontWeight: 900,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
};

const stepTitleStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
  lineHeight: 1.35,
};

const stepDetailStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.5,
  margin: 0,
  overflowWrap: "anywhere",
};

const stepTargetStyle = {
  color: "#93c5fd",
  fontSize: 12,
  fontWeight: 850,
  lineHeight: 1.35,
  overflowWrap: "anywhere",
};

function DetailTile({ label, value }) {
  return (
    <div style={metricStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{displayValue(value)}</span>
    </div>
  );
}

function RecommendedStepCards({ steps = [] }) {
  const rows = Array.isArray(steps) ? steps.filter(Boolean) : [];

  if (!rows.length) {
    return <ExecutionEmptyState title="No remediation steps available" message="No remediation steps are available for this task yet." />;
  }

  return (
    <div style={stepGridStyle}>
      {rows.map((stepItem, index) => (
        <article key={`recommended-step-${index}`} style={stepCardStyle}>
          <div style={stepHeaderStyle}>
            <span style={stepNumberStyle}>{index + 1}</span>
            <div style={{ display: "grid", gap: 5, minWidth: 0 }}>
              <strong style={stepTitleStyle}>{displayValue(stepItem.title, "Remediation step")}</strong>
              {stepItem.detail ? <p style={stepDetailStyle}>{displayValue(stepItem.detail)}</p> : null}
              {stepItem.target ? <span style={stepTargetStyle}>{displayValue(stepItem.target)}</span> : null}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function RemediationGuidanceTab({
  task = {},
  guidanceContext = null,
  guidanceLoading = false,
  guidanceError = "",
  onRefreshGuidance,
  onOpenTab,
}) {
  const guidance = useMemo(() => {
    const context = asObject(guidanceContext);
    const guidancePayload = getGuidancePayload(context);
    const diagnostic = getRulePlanDiagnostic(task, [], task);
    const connector = getConnectorLabel(task, diagnostic, context);
    const validationText = buildValidationText(task);
    const rule = getRuleId(task, context);
    const resource = getResourceId(task, context);

    const rawGuidanceStatus =
      clean(
        context.pathway_name ||
          context.pathway_id ||
          guidancePayload.pathway_name ||
          guidancePayload.pathway_id ||
          guidancePayload.status ||
          task.guidance_status
      ) ||
      clean(diagnostic?.label) ||
      "";

    const validationChecklist = getValidationChecklist(task, validationText, context);

    const selectedSteps = chooseRecommendedStepCards({
      task,
      guidanceContext: context,
      rule,
      connector,
      resource,
      validationChecklist,
    });

    const nextGuidance = {
      diagnostic,
      connector,
      problemSummary: getProblemSummary(task, connector, context),
      riskSummary: getRiskSummary(task, connector, context),
      recommendedSteps: selectedSteps.steps,
      recommendedStepCards: selectedSteps.cards,
      stepSource: selectedSteps.source,
      evidenceRequired: getEvidenceRequired(task, context),
      validationChecklist,
      relatedControls: getRelatedControls(task, context),
      estimatedTime: getEstimatedTime(task, diagnostic, context),
      prerequisites: getPrerequisites(task, context),
      references: getReferences(task, connector, context),
      framework: clean(context.framework || guidancePayload.framework || task.framework || getTaskMetadata(task).framework),
      control: clean(
        context.control_id ||
          context.control ||
          guidancePayload.control_id ||
          guidancePayload.control ||
          task.control_id ||
          task.controlId ||
          getTaskMetadata(task).control_id ||
          getTaskMetadata(task).controlId
      ),
      rule,
      resource,
      guidanceStatus: cleanGuidanceStatus(rawGuidanceStatus, connector, rule),
      source: clean(context.source || guidancePayload.source || selectedSteps.source),
      backendLoaded: Boolean(context.ok || context.source || context.status || guidancePayload.status || guidancePayload.source),
    };

    return nextGuidance;
  }, [guidanceContext, task]);

  return (
    <>
      <ExecutionSection
        title="Remediation Guidance"
        description="Detailed remediation guidance for the selected task."
        badge={guidance.guidanceStatus}
        badgeTone={guidance.backendLoaded ? "default" : guidance.diagnostic?.is_generic_fallback ? "warn" : "default"}
      >
        <div style={gridStyle}>
          <DetailTile label="Connector" value={guidance.connector} />
          <DetailTile label="Framework" value={guidance.framework || "—"} />
          <DetailTile label="Control" value={guidance.control || "—"} />
          <DetailTile label="Rule" value={guidance.rule || "—"} />
          <DetailTile label="Resource" value={guidance.resource || "—"} />
          <DetailTile label="Estimated Time" value={guidance.estimatedTime} />
        </div>

        {guidanceError ? (
          <p style={{ ...paragraphStyle, color: "#fca5a5" }}>
            Guidance context could not be refreshed. Showing available task guidance.
          </p>
        ) : null}

        <ExecutionActionRow
          actions={[
            { key: "refresh", label: guidanceLoading ? "Refreshing…" : "Refresh Guidance", onClick: onRefreshGuidance, disabled: guidanceLoading },
            { key: "validation", label: "Open Validation", onClick: () => onOpenTab?.("validation") },
            { key: "evidence", label: "View Evidence", onClick: () => onOpenTab?.("evidence") },
            { key: "sop", label: "Open SOP", onClick: () => onOpenTab?.("sop") },
          ]}
        />
      </ExecutionSection>

      <ExecutionSection title="Problem Summary" description="Current failing condition.">
        <p style={paragraphStyle}>{guidance.problemSummary}</p>
      </ExecutionSection>

      <ExecutionSection title="Why This Matters" description="Risk and compliance impact.">
        <p style={paragraphStyle}>{guidance.riskSummary}</p>
      </ExecutionSection>

      <ExecutionSection title="Recommended Remediation" description="Steps to resolve this finding.">
        <RecommendedStepCards steps={guidance.recommendedStepCards} />
      </ExecutionSection>

      <ExecutionSection title="Evidence Required" description="Evidence needed before closure.">
        <SectionList items={guidance.evidenceRequired} />
      </ExecutionSection>

      <ExecutionSection title="Validation Checklist" description="How OASYS confirms the fix.">
        <SectionList items={guidance.validationChecklist} />
      </ExecutionSection>

      <ExecutionSection title="Prerequisites" description="Access and approvals needed before making changes.">
        <SectionList items={guidance.prerequisites} />
      </ExecutionSection>

      <ExecutionSection title="Related Controls" description="Mapped framework and control context.">
        {guidance.relatedControls.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {guidance.relatedControls.map((item, index) => (
              <span
                key={`related-control-${index}`}
                style={{
                  border: "1px solid rgba(96, 165, 250, 0.25)",
                  borderRadius: 999,
                  padding: "6px 10px",
                  color: "#bfdbfe",
                  background: "rgba(15, 23, 42, 0.45)",
                  fontSize: 12,
                  fontWeight: 850,
                }}
              >
                {displayValue(item)}
              </span>
            ))}
          </div>
        ) : (
          <ExecutionEmptyState title="No related controls available" message="Only the primary framework/control mapping is available for this task." />
        )}
      </ExecutionSection>

      <ExecutionSection title="References" description="Helpful documentation and internal guidance.">
        <SectionList items={guidance.references} />
      </ExecutionSection>
    </>
  );
}
